import type { ExtensionHandler } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { EXTENSION_TYPE } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';

import { getSsrMigrationExtHandlerForRenderer } from './getSsrMigrationExtHandlerForRenderer.tsx';
import { isInitialLcmSsrEligible, isLegacyContentExtension } from './lcmSsrEligibility.ts';

type NonNullableExtensionHandlers = NonNullable<ADFRendererProps['extensionHandlers']>;

const createFilteredMigrationHandler =
	(delegate: ExtensionHandler): ExtensionHandler =>
	(extension, ...args) => {
		// `null` is the renderer's signal to fall through to the extension manifest, so anything this
		// handler does not own is deliberately dropped here rather than passed to the delegate.
		//
		// An LCM is retained only while it has descendants whose SSR markup has to be preserved. A leaf
		// LCM has nothing below it to keep, so it releases and the manifest becomes its terminal state
		// — at every depth, not just at the outer boundary.
		if (!isLegacyContentExtension(extension) || isInitialLcmSsrEligible(extension)) {
			return null;
		}

		return delegate(extension, ...args);
	};

/**
 * Creates the manifest-ready handler set for LCM hierarchies that were rendered recursively on the
 * server. It retains only LCMs that still have descendants whose SSR markup must be preserved, and
 * passes itself back into each nested renderer so the same component hierarchy survives the client
 * takeover without remounting. Ordinary and leaf LCMs, and unrelated extensions, fall through to
 * their manifests — so the recursion terminates at every depth rather than only at the outer edge.
 *
 * Nested content keeps the handler set that produced the server markup, overriding only the
 * migration entry. Dropping the other handlers here would let macros inside a nested LCM fall
 * through to their manifests, and the manifest renderer re-expands nested structured macros as
 * separate fabric extensions. That re-partitions the DOM: a `section` macro SSRs as one legacy macro
 * element whose HTML carries the `sectionMacro`/`sectionMacroRow` table wrappers for its child
 * `column` macro, so re-rendering the column on its own strands a `display: table-cell` with no
 * table ancestor. The orphaned cell becomes an anonymous shrink-to-fit table box and the column
 * visibly collapses.
 *
 * Retaining those handlers is therefore about DOM stability, not ownership — so the retained set is
 * the *releasing* one (`releaseEmptyMacrosToManifest`). Macros the SSR path renders keep their server
 * markup; macros it declines return `null` and reach their manifest, which is what keeps
 * "SSR -> eventually -> manifest" true for everything inside the hierarchy.
 */
export const createNestedLcmHydrationExtensionHandlers = ({
	annotationProvider,
	contentId,
	contentMode,
	nestedExtensionHandlers,
	serverExtensionHandlers,
}: {
	annotationProvider?: ADFRendererProps['annotationProvider'];
	contentId: string;
	contentMode?: EditorContentMode;
	/**
	 * Handler set for nested content once the manifest is ready: same macro rendering as the server,
	 * but declining the macros it does not own so they can reach their manifests.
	 */
	nestedExtensionHandlers: NonNullableExtensionHandlers;
	/** Handler set that produced the server markup; in effect until the client config lands. */
	serverExtensionHandlers: NonNullableExtensionHandlers;
}): NonNullableExtensionHandlers => {
	let nestedClientExtensionHandlers: NonNullableExtensionHandlers;
	const migrationHandler = getSsrMigrationExtHandlerForRenderer({
		clientAnnotationProvider: annotationProvider,
		clientHydrationReady: true,
		contentId,
		contentMode,
		getLcmClientExtensionHandlers: () => nestedClientExtensionHandlers,
		getLcmNestedExtensionHandlers: () => serverExtensionHandlers,
		nestedLcmSsrEnabled: true,
	});

	const migrationEntry = createFilteredMigrationHandler(migrationHandler);

	nestedClientExtensionHandlers = {
		...nestedExtensionHandlers,
		[EXTENSION_TYPE.MIGRATION]: migrationEntry,
	};

	return {
		[EXTENSION_TYPE.MIGRATION]: migrationEntry,
	};
};
